"use client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";

import ColorSchemesSelector from "./color-schemes-selector";
import { Tabs, TabsContent, TabsList } from "@/components/ui/tabs";
import { CustomTabsTriggers } from "@/components/ui/custom-tab-triggers";
import { ColorPaletteIcon } from "@/app/icons/icon-color-palette";
import { FontIcon } from "@/app/icons/icon-font";
import { WordByWordIcon } from "@/app/icons/icon-word-by-word";
import { TranslationIcon } from "@/app/icons/icon-translation";
import { PlayerIcon } from "@/app/icons/icon-player";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dispatch, SetStateAction, useMemo, useRef, useState } from "react";
import { AArrowDown, AArrowUp, ArrowLeft, ListRestart } from "lucide-react";

import { DropdownListType, QuranMushaf } from "@/lib/types";
import {
  useQuranOptions,
  useQuranStateUpdate,
} from "@/app/providers/QuranContextProvider";
import { Card, CardContent, CardFooter, CardHeader } from "../card";
import { Button, buttonVariants } from "../button";
import QuranFontPreview from "../quran-font-preview";
import { cn } from "@/lib/helpers/utils";
import { fontStyleDropdown } from "@/lib/data/uthmani-font-style";
import { indoPakList } from "@/lib/data/indopak-lines";
import { tabs } from "@/lib/data/settings-tabs-list";
import { wfwLanguageList } from "@/lib/data/wfw-language-list";
import { AnimatePresence, motion } from "framer-motion";

const QuranAppSettings = ({ children }: { children: React.ReactNode }) => {
  const {
    activeTranslations,
    quranMushaf,
    showQuarnTranslation,
    quranFont,
    showTooltip,
    showWordTranslations,
    showWordTransliteration,
    showInsline,
    wfwRecitation,
  } = useQuranOptions();

  const {
    setShowWordTranslations,
    setShowQuarnTranslation,
    setShowWordTransliteration,
    setShowTooltip,
    setShowInsline,
    setWfwRecitation,
    setActiveTranslations,
  } = useQuranStateUpdate();

  const [listTranslations, setListTranslations] = useState(false);

  let activeTab = "Uthmani";
  switch (quranMushaf.type) {
    case "hafs1":
      break;
    case "hafs2":
      if (quranFont == "hafs2" || quranFont == "uhfs") activeTab = "Uthmani";
      else activeTab = "Tajweed";
      break;
    case "indopak15":
    case "indopak16":
      activeTab = "Indo-Pak";
      break;
    default:
  }

  return (
    <>
      <AnimatePresence mode="popLayout">
        {listTranslations && (
          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: "0", opacity: 1 }}
            transition={{ duration: 0.25 }}
            exit={{ x: "100", opacity: 0 }}
          >
            <div className="flex justify-between">
              <Button
                onClick={() => setListTranslations((p) => !p)}
                variant="bgGhost"
                className="rounded-full w-24 flex flex-row"
              >
                <ArrowLeft className="w-5 h-5" /> <span> Back </span>
              </Button>

              <Button
                onClick={() => setActiveTranslations([])}
                variant="bgGhost"
                className="rounded-full flex flex-row"
              >
                <span> Reset selections </span>
                <ListRestart className="w-5 h-5" />
              </Button>
            </div>
            {children}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode="popLayout">
        {!listTranslations && (
          <motion.div
            initial={{ x: "-100%", opacity: 0 }}
            animate={{ x: "0", opacity: 1 }}
            transition={{ duration: 0.25 }}
            exit={{ x: "-100", opacity: 0 }}
          >
            <Accordion
              defaultValue="color-schemes"
              type="single"
              collapsible
              className="AccordionDiv"
            >
              <AccordionItem value="color-schemes">
                <AccordionTrigger className="hover:bg-primary/10 pl-3 pr-3 rounded-md">
                  <div className="flex flex-row p ">
                    <ColorPaletteIcon className="w-5 h-5 text-primary" />
                    <div className="ml-3">Color Schemes</div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <ColorSchemesSelector />
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="fonts2">
                <AccordionTrigger className="hover:bg-primary/10 pl-3 pr-3 rounded-md">
                  <div className="flex flex-row ">
                    <FontIcon className="w-5 h-5 text-primary" />
                    <div className="ml-3">Quran Fonts</div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="pt-4">
                    <Tabs
                      defaultValue={activeTab}
                      className="w-full flex flex-col justify-center flex-wrap "
                    >
                      <TabsList className="flex flex-row justify-center items-center relative ">
                        <CustomTabsTriggers
                          className="w-1/3"
                          tabList={tabs}
                          tabIndicatorId={"indicator-quran-fonts"}
                        />
                      </TabsList>
                      <TabsContent value="Uthmani">
                        <div className="flex flex-col w-full">
                          <div className=" flex flex-col  pt-4 justify-between items-center w-full">
                            <div className="w-full">Font Style</div>
                            <div className="pt-4 w-5/6 md:w-2/3">
                              <DropDownWrapper
                                type={"font"}
                                items={fontStyleDropdown}
                              />
                            </div>
                          </div>
                        </div>
                      </TabsContent>
                      <TabsContent value="Indo-Pak" className="h-auto">
                        <div className=" flex flex-col pt-4 justify-between items-center w-full">
                          <div className="w-full">Lines</div>
                          <div className="pt-4 w-5/6 md:w-2/3">
                            <DropDownWrapper
                              type={"lines"}
                              items={indoPakList}
                            />
                          </div>
                        </div>
                      </TabsContent>
                    </Tabs>
                    <div className=" flex flex-col pt-4 justify-between items-center w-full">
                      <div className="w-full">Font Size</div>
                      <div className="pt-3 w-5/6 md:w-2/3">
                        <FontSizeSelect name={"Quran"} />
                      </div>
                    </div>
                    <div className=" flex flex-row pt-4 justify-between items-center w-full relative">
                      <div className="w-1/4 h-[9px] absolute top-4 left-4 bg-transparent text-center ">
                        <div className="relative top-[-8px]">Preview</div>
                      </div>

                      <div className="w-full h-40 bg-primary/10 border-2 rounded-lg">
                        <QuranFontPreview />
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="wfw">
                <AccordionTrigger className="hover:bg-primary/10 pl-3 pr-3 rounded-md">
                  <div className="flex flex-row ">
                    <WordByWordIcon className="w-5 h-5 text-primary" />
                    <div className="ml-3">Word for Word</div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col">
                    <div className=" flex flex-col pt-4 justify-between items-center w-full">
                      <div className="w-full">Language</div>
                      <div className="pt-4 w-5/6 md:w-2/3">
                        <DropDownWrapper type={"wfw"} items={wfwLanguageList} />
                      </div>
                    </div>
                    <SwitchLabelWrapper
                      label="Translation"
                      isOn={showWordTranslations}
                      onClick={setShowWordTranslations}
                    />
                    <SwitchLabelWrapper
                      label="Transliteration"
                      isOn={showWordTransliteration}
                      onClick={setShowWordTransliteration}
                    />
                    <SwitchLabelWrapper
                      label="Recitation"
                      isOn={wfwRecitation}
                      onClick={setWfwRecitation}
                    />
                    <SwitchLabelWrapper
                      label="Tooltip"
                      isOn={showTooltip}
                      onClick={setShowTooltip}
                    />
                    <SwitchLabelWrapper
                      label="Inline"
                      isOn={showInsline}
                      onClick={setShowInsline}
                    />
                    <div className=" flex flex-col pt-6 justify-between items-center w-full">
                      <div className="w-full">Font Size</div>
                      <div className="pt-3 w-5/6 md:w-2/3">
                        <FontSizeSelect name="WordByWord" />
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="translations">
                <AccordionTrigger className="hover:bg-primary/10 pl-3 pr-3 rounded-md">
                  <div className="flex flex-row ">
                    <TranslationIcon className="w-5 h-5 text-primary" />
                    <div className="ml-3">Translations</div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col">
                    <div className=" flex flex-row pt-6 justify-between items-center w-full">
                      <Label htmlFor="ayah-trans">Ayah Translation(s)</Label>
                      <Switch
                        id="ayah-htrans"
                        checked={showQuarnTranslation}
                        onClick={() => setShowQuarnTranslation((p) => !p)}
                      />
                    </div>

                    <div className=" flex flex-row pt-6 justify-center items-center w-full">
                      <Card
                        className="w-full bg-transparent border-0 hover:bg-primary/10 cursor-pointer"
                        onClick={() => setListTranslations((p) => !p)}
                      >
                        <CardContent className="p-0">
                          <div className="w-full bg-primary/5 p-4 rounded-md font-bold flex flex-col">
                            <div className="text-primary/50 text-[12px]">
                              Selected Translations
                            </div>
                            <div className="text-primary/80">
                              {activeTranslations.map(
                                (activeTranslation, index) => {
                                  return index < 2 ? (
                                    <span key={"active-now" + index}>
                                      {activeTranslation.name},&nbsp;
                                    </span>
                                  ) : (
                                    <span key={"active-others"}>
                                      and {activeTranslations.length - 2}
                                      &nbsp; other
                                    </span>
                                  );
                                },
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="player">
                <AccordionTrigger className="hover:bg-primary/10 pl-3 pr-3 rounded-md">
                  <div className="flex flex-row ">
                    <PlayerIcon className="w-5 h-5 text-primary" />
                    <div className="ml-3">Player</div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  Please check back later.
                  {/* Lorem ipsum dolor sit amet, officia excepteur ex fugiat */}
                  {/* reprehenderit enim labore culpa sint ad nisi Lorem pariatur */}
                  {/* mollit ex esse exercitation amet. Nisi anim cupidatat */}
                  {/* excepteur officia. Reprehenderit nostrud nostrud ipsum Lorem */}
                  {/* est aliquip amet voluptate voluptate dolor minim nulla est */}
                  {/* proident. Nostrud officia pariatur ut officia. Sit irure elit */}
                  {/* esse ea nulla sunt ex occaecat reprehenderit commodo officia */}
                  {/* dolor Lorem duis laboris cupidatat officia voluptate. Culpa */}
                  {/* proident adipisicing id nulla nisi laboris ex in Lorem sunt */}
                  {/* duis officia eiusmod. Aliqua reprehenderit commodo ex non */}
                  {/* excepteur duis sunt velit enim. Voluptate laboris sint */}
                  {/* cupidatat ullamco ut ea consectetur et est culpa et culpa */}
                  {/* duis. */}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default QuranAppSettings;
QuranAppSettings.displayName = "QuranAppSettings";

const DropDownWrapper: React.FC<{
  type: string;
  items: Array<DropdownListType>;
}> = ({ type, items }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { quranMushaf, quranFont, wfwLanguage } = useQuranOptions();
  const { setQuranFont, setQuranMushaf, setWfwLanguage } =
    useQuranStateUpdate();

  const value = useMemo(() => {
    switch (type) {
      case "wfw":
        return wfwLanguage;
      case "lines":
        return `${quranMushaf.lines}-lines`;
      default:
        return quranFont;
    }
  }, [type, wfwLanguage, quranMushaf.lines, quranFont]);

  const selectedValue = useMemo(() => {
    let selectedItem;
    switch (type) {
      case "wfw":
        selectedItem = items.find((item) => item.value === wfwLanguage);
        return selectedItem ? selectedItem.name : "";
      case "lines":
        return `${quranMushaf.lines} Lines`;
      default:
        selectedItem = items.find((item) => item.value === quranFont);
        return selectedItem ? selectedItem.name : "";
    }
  }, [type, wfwLanguage, quranMushaf.lines, quranFont]);

  const handleItemClick = (item: string) => {
    console.log("item: ", item);
    switch (item) {
      case "16-lines":
        setQuranMushaf((prev: QuranMushaf) => ({
          type: "indopak16",
          lines: parseInt(item),
        }));
        break;
      case "15-lines":
        setQuranMushaf((prev: QuranMushaf) => ({
          type: "indopak15",
          lines: parseInt(item),
        }));
        break;
      case "hafs1":
      case "hafs2":
        setQuranFont(item);
        const type = item;
        setQuranMushaf((prev) => ({
          ...prev,
          type,
        }));
        break;
      case "uhfs":
        setQuranFont(item);
        break;
      default:
        setWfwLanguage(item);
    }
  };

  return (
    <Select
      open={isDropdownOpen}
      onOpenChange={() => setIsDropdownOpen((prev) => !prev)}
      value={value}
      onValueChange={(e) => handleItemClick(e)}
    >
      <SelectTrigger
        className={cn(
          `${buttonVariants({ variant: isDropdownOpen ? "default" : "outline" })} ${isDropdownOpen ? "" : "bg-primary/10 "}`,
          "hover:bg-primary/30",
        )}
      >
        <div className="w-full">{selectedValue}</div>
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {items.map((item) => (
            <SelectItem
              key={item.id + item.value}
              value={item.value}
              className="text-md"
            >
              {item.name}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

const FontSizeSelect = ({ name }: { name: string }) => {
  const { quranFontSize, style } = useQuranOptions();
  const { setQuranFontSize, setWfwFontSize } = useQuranStateUpdate();
  const ref = useRef<HTMLElement>(null);
  const handleSliderChange = (value: number[], name: string) => {
    switch (name) {
      case "Quran":
        setQuranFontSize(value[0]);
        break;
      case "WordByWord":
        setWfwFontSize(value[0]);
        break;
      default:
    }
  };

  let defVal = [0];
  if (name == "Quran") {
    defVal = [quranFontSize];
  }
  if (name == "WordByWord") {
    defVal = [1];
  }
  return (
    <div
      onPointerMove={(e) => {
        e.stopPropagation();
      }}
      className="flex flex-row justify-center items-center"
    >
      <AArrowDown className="mr-2 w-9 h-9 text-primary" />
      <Slider
        ref={ref}
        defaultValue={defVal}
        max={2.75}
        step={0.25}
        min={1.5}
        className="h-6"
        onValueChange={(value) => handleSliderChange(value, name)}
      />
      <AArrowUp className="ml-2 w-9 h-9 text-primary" />
    </div>
  );
};
const SwitchLabelWrapper = ({
  label,
  onClick,
  isOn,
}: {
  label: string;
  onClick: Dispatch<SetStateAction<boolean>>;
  isOn: boolean;
}) => (
  <div className=" flex flex-row pt-6 justify-between items-center w-full">
    <Label htmlFor={label}>{label}</Label>
    <Switch id={label} onClick={() => onClick((p) => !p)} checked={isOn} />
  </div>
);

